/**
 * Optimistic concurrency for CRF form saves - the page's half.
 *
 * The CRF Builder saves the WHOLE form from its own copy on every edit. On
 * 2026-09-10 a copy loaded before a dictionary adoption was saved nine minutes
 * after it, and put the Adverse Event form back exactly as it had been: 16
 * renamed variables reverted, two added questions removed. The server now
 * refuses a save whose base version has moved (saveCrfFormInternal,
 * `expectedUpdatedAt`). This module is what the page needs to use that.
 *
 * Pure - no React, no Firebase - so it can be tested from functions' vitest.
 */

/** Shown when the server refuses a save because the form moved underneath it. */
export const FORM_CHANGED_MESSAGE =
  'This form was changed elsewhere after you opened it - for example by a dictionary ' +
  'adoption or in another tab. Your change was not saved. Reload the page to see the ' +
  'current version, then make your change again.';

/**
 * The version a save should claim it was based on: the LATER of the two the
 * page knows about - what the last successful save returned, and what the most
 * recent server read said. Both are ISO strings from Date.toISOString(), which
 * compare correctly as strings.
 *
 * Undefined when the page knows neither, which is a form the server has never
 * seen (created on this page). The server does not check a save with no base,
 * so a brand-new form can always be written the first time.
 */
export function chooseSaveBase(
  fromLastSave: string | undefined,
  fromServerRead: string | undefined,
): string | undefined {
  if (fromLastSave === undefined) return fromServerRead;
  if (fromServerRead === undefined) return fromLastSave;
  return fromLastSave >= fromServerRead ? fromLastSave : fromServerRead;
}

/**
 * Is this the server refusing a stale save? The web SDK spells the code
 * `functions/failed-precondition`; a raw HttpsError spells it without the
 * prefix. Both are accepted, and the reason must say `form-changed` so that any
 * other precondition failure is not mistaken for this one.
 */
export function isFormChangedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; details?: unknown };
  const code = typeof e.code === 'string' ? e.code.replace(/^functions\//, '') : '';
  const details = e.details && typeof e.details === 'object'
    ? (e.details as { reason?: unknown })
    : undefined;
  return code === 'failed-precondition' && details?.reason === 'form-changed';
}

/**
 * Is this the server refusing a name that another form already uses? The web
 * SDK spells the code `functions/already-exists`; a raw HttpsError spells it
 * without the prefix. The CRF Builder used to compare against the raw spelling
 * only, so the server's specific message was never shown and a name clash read
 * as a generic "please retry", which no retry can fix.
 */
export function isAlreadyExistsError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && code.replace(/^functions\//, '') === 'already-exists';
}

/**
 * Run `task` after every earlier save of the same form has settled - success
 * or failure - and return the task's own result, so the caller still sees its
 * own save fail. Saves of different forms do not wait on each other.
 *
 * The map holds, per form, a promise for that form's latest queued save that
 * never rejects, so one failed save cannot stop the next from running.
 */
export function enqueueSave<T>(
  queue: Map<string, Promise<unknown>>,
  key: string,
  task: () => Promise<T>,
): Promise<T> {
  const previous = queue.get(key) ?? Promise.resolve();
  const run = previous.then(task, task);
  queue.set(key, run.catch(() => undefined));
  return run;
}
